import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik, Field, Form } from 'formik';
import { withTheme } from 'styled-components';
import { TextInput, Button, Checkbox } from '@01_people/ui-library';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router';

import CountrySelect from 'components/ui/country-select';
import * as ActionCreators from '../../../redux/action';
import generateValidationScheme from './personal-form.schema';
import PersonalFormSC from './personal-form.styled';
import InfoListItem from '../../info-banner';
import TextInfo from '../../info-banner/components/textinfo';
import { generateInitialFormValues } from './personal-form.utils';

export const PersonalForm = props => {
  const {
    user,
    nationalities,
    countries,
    updateUserProfile,
    toggleInfoModal,
    t,
    customFields,
    ui
  } = props;
  const customFieldsKeys = useMemo(() => Object.keys(customFields), [
    customFields
  ]);
  const schema = useMemo(() => generateValidationScheme(customFields), [
    customFields
  ]);
  const initialValues = useMemo(() => generateInitialFormValues(user), [user]);
  const profileDisabled = useMemo(
    () =>
      ui && ui.modules && ui.modules.includes('kyc')
        ? user.kyc.status !== 'unUploaded'
        : false,
    [user, ui]
  );

  return (
    <PersonalFormSC id="personal-info">
      <h5>{t('profile.personal.title')}</h5>
      {profileDisabled && (
        <div className="kyc-disabled-info">
          <InfoListItem accent>
            <TextInfo
              text={t('profile.personal.kycDisabledInfo')}
              styling="info"
            />
          </InfoListItem>
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize
        onSubmit={(values, { setSubmitting, resetForm }) => {
          const data = values;
          const valueKeys = Object.keys(values);
          const refs = {};

          valueKeys.forEach(key => {
            if (key.match(/^ref[0-9]$/)) {
              refs[key] = values[key] === '' ? null : values[key];
              delete data[key];
            }
          });

          data.refs = refs;
          data.middleName = null;
          data.callback = () => setSubmitting(false);
          data.button = {
            text: 'Ok',
            callback: toggleInfoModal
          };

          updateUserProfile(data);
          resetForm();
        }}
        render={({
          values,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          isSubmitting,
          dirty
        }) => (
          <Form>
            <Prompt when={dirty} message={t('profile.leaveMessage')} />
            <div className="personal">
              <div className="personal-left">
                <h6>{t('profile.personal.user.title')}</h6>
                <p className="color-description">
                  {t('profile.personal.user.description')}
                </p>
              </div>
              <div className="personal-right">
                <Field
                  type="text"
                  name="userName"
                  label={t('profile.personal.user.form.username')}
                  customClass="txt-inp"
                  component={TextInput}
                  defaultValue={user.username}
                  onChange={value => setFieldValue('userName', value)}
                  onBlur={() => setFieldTouched('userName', true)}
                  setTouched={value => setFieldTouched('userName', value)}
                  errors={
                    errors.userName && touched.userName
                      ? [t(errors.userName)]
                      : []
                  }
                />

                <div className="name">
                  <Field
                    type="text"
                    name="firstName"
                    label={t('profile.personal.user.form.firstName')}
                    customClass="txt-inp"
                    component={TextInput}
                    defaultValue={user.firstName}
                    onChange={value => setFieldValue('firstName', value)}
                    onBlur={() => setFieldTouched('firstName', true)}
                    setTouched={value => setFieldTouched('firstName', value)}
                    errors={
                      errors.firstName && touched.firstName
                        ? [t(errors.firstName)]
                        : []
                    }
                    disabled={profileDisabled}
                  />
                  <Field
                    type="text"
                    name="lastName"
                    label={t('profile.personal.user.form.lastName')}
                    customClass="txt-inp"
                    component={TextInput}
                    defaultValue={user.lastName}
                    onChange={value => setFieldValue('lastName', value)}
                    onBlur={() => setFieldTouched('lastName', true)}
                    setTouched={value => setFieldTouched('lastName', value)}
                    errors={
                      errors.lastName && touched.lastName
                        ? [t(errors.lastName)]
                        : []
                    }
                    disabled={profileDisabled}
                  />
                </div>

                <Field
                  name="nationality"
                  component={CountrySelect}
                  customClass="txt-inp"
                  value={values.nationality}
                  defaultValue={user.nationality}
                  countryCodes={nationalities}
                  language={localStorage.getItem('current_lang')}
                  onChange={value => {
                    setFieldValue(
                      'nationality',
                      value !== null ? value.value : null
                    );
                  }}
                  placeholder={t('profile.personal.user.form.nationality')}
                  disabled={profileDisabled}
                  noOptionsMessage={() => t('components:select.no_options')}
                  errors={
                    errors.nationality && touched.nationality
                      ? [t(errors.nationality)]
                      : []
                  }
                />
              </div>
            </div>
            <div className="address">
              <span className="max-small section-heading">
                {t('profile.personal.address.title')}
              </span>
              <div className="address-left">
                <h6>{t('profile.personal.address.title')}</h6>
              </div>
              <div className="address-right">
                <div className="street-num">
                  <Field
                    type="text"
                    name="city"
                    label={t('profile.personal.address.form.city')}
                    customClass="without-mb-20 txt-inp"
                    component={TextInput}
                    defaultValue={user.address.city}
                    onChange={value => setFieldValue('city', value)}
                    onBlur={() => setFieldTouched('city', true)}
                    setTouched={value => setFieldTouched('city', value)}
                    renderErrors={false}
                    errors={errors.city && touched.city ? [t(errors.city)] : []}
                    disabled={profileDisabled}
                  />
                  <Field
                    type="text"
                    name="zip"
                    label={t('profile.personal.address.form.zip')}
                    customClass="without-mb-20 txt-inp"
                    component={TextInput}
                    defaultValue={user.address.zip}
                    onChange={value => setFieldValue('zip', value)}
                    onBlur={() => setFieldTouched('zip', true)}
                    setTouched={value => setFieldTouched('zip', value)}
                    renderErrors={false}
                    errors={errors.zip && touched.zip ? t(errors.zip) : []}
                    disabled={profileDisabled}
                  />
                </div>

                {errors.zip && touched.zip}
                {(errors.city && touched.city) ||
                (errors.zip && touched.zip) ? (
                  <div className="text-error">{`${
                    errors.city ? t(errors.city) : ''
                  } ${errors.zip ? t(errors.zip) : ''}`}</div>
                ) : (
                  <div className="mb-20" />
                )}

                <Field
                  type="text"
                  name="streetAndNumber"
                  label={t('profile.personal.address.form.street')}
                  customClass="txt-inp"
                  component={TextInput}
                  defaultValue={user.address.street}
                  onChange={value => setFieldValue('streetAndNumber', value)}
                  onBlur={() => setFieldTouched('streetAndNumber', true)}
                  setTouched={value =>
                    setFieldTouched('streetAndNumber', value)
                  }
                  errors={
                    errors.streetAndNumber && touched.streetAndNumber
                      ? [t(errors.streetAndNumber)]
                      : []
                  }
                  disabled={profileDisabled}
                />
                <Field
                  name="country"
                  component={CountrySelect}
                  customClass="txt-inp"
                  value={values.country}
                  defaultValue={user.address.country}
                  countryCodes={countries}
                  language={localStorage.getItem('current_lang')}
                  onChange={value => {
                    setFieldValue(
                      'country',
                      value !== null ? value.value : null
                    );
                  }}
                  placeholder={t('profile.personal.address.form.country')}
                  disabled={profileDisabled}
                  noOptionsMessage={() => t('components:select.no_options')}
                  errors={
                    errors.country && touched.country ? [t(errors.country)] : []
                  }
                />
              </div>
            </div>
            {/* TODO Should be more inteligent %4 years and so... */}
            <div className="birthday">
              <span className="max-small section-heading">
                {t('profile.personal.birth.title')}
              </span>
              <div className="birthday-left">
                <h6>{t('profile.personal.birth.title')}</h6>
              </div>
              <div className="birthday-right">
                <Field
                  type="text"
                  name="dayOfBirthday"
                  customClass="without-mb-20"
                  label={t('profile.personal.birth.form.day')}
                  component={TextInput}
                  defaultValue={user.birthday.day}
                  onChange={value => setFieldValue('dayOfBirthday', value)}
                  onBlur={() => setFieldTouched('dayOfBirthday', true)}
                  setTouched={value => setFieldTouched('dayOfBirthday', value)}
                  renderErrors={false}
                  errors={errors.dayOfBirthday ? [t(errors.dayOfBirthday)] : []}
                  disabled={profileDisabled}
                />

                <Field
                  type="text"
                  name="monthOfBirthday"
                  customClass="without-mb-20"
                  label={t('profile.personal.birth.form.month')}
                  component={TextInput}
                  defaultValue={user.birthday.month}
                  onChange={value => setFieldValue('monthOfBirthday', value)}
                  onBlur={() => setFieldTouched('monthOfBirthday', true)}
                  setTouched={value =>
                    setFieldTouched('monthOfBirthday', value)
                  }
                  renderErrors={false}
                  errors={
                    errors.monthOfBirthday ? [t(errors.monthOfBirthday)] : []
                  }
                  disabled={profileDisabled}
                />

                <Field
                  type="text"
                  name="yearOfBirthday"
                  customClass="without-mb-20"
                  label={t('profile.personal.birth.form.year')}
                  component={TextInput}
                  defaultValue={user.birthday.year}
                  onChange={value => setFieldValue('yearOfBirthday', value)}
                  onBlur={() => setFieldTouched('yearOfBirthday', true)}
                  setTouched={value => setFieldTouched('yearOfBirthday', value)}
                  renderErrors={false}
                  errors={
                    errors.yearOfBirthday ? [t(errors.yearOfBirthday)] : []
                  }
                  disabled={profileDisabled}
                />
              </div>
            </div>
            {(errors.dayOfBirthday && touched.dayOfBirthday) ||
            (errors.monthOfBirthday && touched.monthOfBirthday) ||
            (errors.yearOfBirthday && touched.yearOfBirthday) ? (
              <div className="text-error">
                {errors.dayOfBirthday && touched.dayOfBirthday ? (
                  <p>{t(errors.dayOfBirthday)}</p>
                ) : null}
                {errors.monthOfBirthday && touched.monthOfBirthday ? (
                  <p>{t(errors.monthOfBirthday)}</p>
                ) : null}
                {errors.yearOfBirthday && touched.yearOfBirthday ? (
                  <p>{t(errors.yearOfBirthday)}</p>
                ) : null}
              </div>
            ) : (
              <div className="mb-20" />
            )}
            <div className="other">
              <span className="max-small section-heading">
                {t('profile.personal.pep.title')}
              </span>
              <div className="pep-left">
                <h6>{t('profile.personal.pep.title')}</h6>
              </div>
              <div className="pep-right">
                <Checkbox
                  label={t('profile.personal.pep.label')}
                  checked={values.pep}
                  onChange={ch => setFieldValue('pep', ch)}
                  onBlur={() => setFieldTouched('pep')}
                />
              </div>
            </div>
            {customFieldsKeys.map(key => (
              <div key={key} className="other">
                <span className="max-small section-heading">
                  {customFields[key].placeholder_translated}
                </span>
                <div className="other-left">
                  <h6>{customFields[key].placeholder_translated}</h6>
                </div>
                <div className="other-right">
                  <Field
                    type="text"
                    key={key}
                    name={key}
                    label={customFields[key].placeholder_translated}
                    component={TextInput}
                    customClass="txt-inp"
                    defaultValue={user.refs[key] || undefined}
                    onChange={value => setFieldValue(key, value)}
                    onBlur={() => setFieldTouched(key, true)}
                    setTouched={value => setFieldTouched(key, value)}
                    errors={errors[key] ? [t(errors[key])] : []}
                    disabled={!customFields[key].editable}
                  />
                </div>
              </div>
            ))}
            <div className="save-wrapper">
              <Button
                styling="primary"
                type="submit"
                // onClick={() => console.log(values)}
                label={t('profile.personal.saveButton')}
                disabled={Object.keys(errors).length !== 0 || isSubmitting}
              />
            </div>
          </Form>
        )}
      />
    </PersonalFormSC>
  );
};

PersonalForm.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address: PropTypes.shape({
      city: PropTypes.string,
      zip: PropTypes.string,
      street: PropTypes.string,
      country: PropTypes.string
    }),
    birthday: PropTypes.shape({
      day: PropTypes.string,
      month: PropTypes.string,
      year: PropTypes.string
    }),
    refs: PropTypes.shape({
      [PropTypes.string]: PropTypes.string
    }),
    kyc: PropTypes.shape({
      status: PropTypes.string
    })
  }).isRequired,
  updateUserProfile: PropTypes.func.isRequired,
  toggleInfoModal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  customFields: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      type: PropTypes.string,
      require: PropTypes.bool,
      editable: PropTypes.bool,
      placeholder_key: PropTypes.string,
      placeholder_translated: PropTypes.string
    })
  })
};

PersonalForm.defaultProps = {
  customFields: {}
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateUserProfile: ActionCreators.updateUserProfile,
      toggleInfoModal: ActionCreators.toggleInfoModal
    },
    dispatch
  );

export default withTranslation('pages')(
  withRouter(
    connect(
      state => state,
      mapDispatchToProps
    )(withTheme(React.memo(PersonalForm)))
  )
);
